import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { shiftDays, today, type IsoDate } from '@/lib/dates'
import type { JobKind } from '@/lib/jobs'
import { describeError, supabase, type Row } from '@/lib/supabase'

/**
 * Making a document from the New … form (29 Oct 2026): the client (made on
 * the spot if new), the project (an existing one, a new one, or none), then
 * the document itself, blank or from a template.
 */

export type DocumentKind = 'quote' | 'invoice' | 'contract' | 'questionnaire'

export type ProjectChoice =
  | { mode: 'existing'; jobId: string }
  | { mode: 'new'; name: string; kind: JobKind; date: IsoDate | '' }
  | { mode: 'none' }

/** Adds a client from the New … form and returns their id. */
export function useCreateClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { first_name: string; last_name: string | null; email: string | null; phone: string | null }) => {
      const { data, error } = await supabase.from('contacts').insert(input).select('id').single()
      if (error) throw error
      return data.id
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts'] }),
    onError: (error) => toast.error('Could not add the client', { description: describeError(error) }),
  })
}

/** The project the document belongs to: made now if it is new. Null for no project. */
export async function resolveProject(choice: ProjectChoice, contactId: string, staffId: string): Promise<string | null> {
  if (choice.mode === 'none') return null
  if (choice.mode === 'existing') return choice.jobId
  // The column follows from the date (jobs_auto_stage); the stage given here
  // is only the placeholder the trigger replaces, as on New project.
  const { data: stage, error: stageError } = await supabase
    .from('pipeline_stages')
    .select('id')
    .is('archived_at', null)
    .order('sort_order')
    .limit(1)
    .single()
  if (stageError) throw stageError
  const { data, error } = await supabase
    .from('jobs')
    .insert({
      primary_contact_id: contactId,
      pipeline_stage_id: stage.id,
      event_date: choice.date,
      kind: choice.kind,
      title: choice.name.trim() || null,
      created_by: staffId,
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

/** A draft quote; from a collection it starts with that collection's line. */
export async function createQuote(input: {
  contactId: string
  jobId: string | null
  staffId: string
  validityDays: number
  fromPackage: Row<'packages'> | null
}): Promise<string> {
  const price = input.fromPackage ? Number(input.fromPackage.base_price) : 0
  const { data, error } = await supabase
    .from('quotes')
    .insert({
      contact_id: input.contactId,
      job_id: input.jobId,
      package_id: input.fromPackage?.id ?? null,
      valid_until: shiftDays(today(), input.validityDays),
      subtotal: price,
      total: price,
      created_by: input.staffId,
    })
    .select('id')
    .single()
  if (error) throw error
  if (input.fromPackage) {
    const { error: lineError } = await supabase.from('quote_line_items').insert({
      quote_id: data.id,
      source: 'package',
      source_id: input.fromPackage.id,
      description: input.fromPackage.name,
      detail: input.fromPackage.tagline ?? null,
      quantity: 1,
      unit_price: price,
      line_total: price,
      sort_order: 0,
    })
    if (lineError) throw lineError
  }
  return data.id
}

/** A blank draft invoice, due in 30 days. Its lines are added in the editor. */
export async function createInvoice(input: { contactId: string; jobId: string | null; staffId: string }): Promise<string> {
  const { data, error } = await supabase
    .from('invoices')
    .insert({
      contact_id: input.contactId,
      job_id: input.jobId,
      kind: 'standard',
      issue_date: today(),
      due_date: shiftDays(today(), 30),
      created_by: input.staffId,
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

export async function createContract(input: { contactId: string; jobId: string | null; staffId: string; title: string }): Promise<string> {
  const { data, error } = await supabase
    .from('contracts')
    .insert({ contact_id: input.contactId, job_id: input.jobId, title: input.title, created_by: input.staffId })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}
